/**
 * Ghosts — after the last chord, up to 3 candidate next-chords: expected,
 * deceptive, color (founding spec feature area F).
 *
 * Reuses the existing "Natural Nexts" harmonic grammar (src/lib/harmonic-grammar.ts)
 * for expected/deceptive rather than re-deriving progression logic, and follows
 * the moves engine's darken-it.ts convention for the borrowed/mixture 'color'
 * candidate (same scale-degree quality-flip, chromaticType: 'borrowed', no
 * altered-root invention — see MIXTURE_TARGETS below).
 */
import type { Chord, ChordExtensions, ChordQuality, Mode, MusicalKey, ScaleDegree } from '@/types/chord';
import { getSuggestions, type ChordSuggestion } from '@/lib/harmonic-grammar';
import { getNaturalQuality } from '@/utils/chord-helpers';
import type { GhostCandidate, GhostChordPayload } from './types';

export type { GhostCandidate, GhostChordPayload } from './types';

export interface SuggestNextChordsContext {
  /** Full progression so far, beat-ordered. Empty means "what should open the piece". */
  chords: Chord[];
  key: MusicalKey;
  mode: Mode;
}

/**
 * Mixture-eligible scale degrees and the quality they borrow into, mirroring
 * src/lib/moves/moves/darken-it.ts exactly (same two degrees, same quality
 * flip, same root-fidelity caveat: degree 6's "borrowed VI" keeps vi's
 * diatonic root rather than a true flatted bVI — see that file's comments).
 * Major mode only: darken-it itself has no minor-mode mixture map (borrowing
 * from the parallel major, rather than parallel minor, isn't implemented
 * anywhere in this codebase yet), so 'color' candidates are omitted in minor
 * keys rather than inventing an unsupported mapping.
 */
const MIXTURE_TARGETS: Partial<Record<ScaleDegree, ChordQuality>> = {
  4: 'minor', // IV -> iv
  6: 'major', // vi -> "VI" (root unchanged; see darken-it.ts)
};

/** Same "is this the dominant" test src/lib/moves/moves/delay-the-arrival.ts uses. */
function isDominant(chord: Chord): boolean {
  return chord.scaleDegree === 5 && (chord.quality === 'major' || chord.quality === 'dom7');
}

function toPayload(
  scaleDegree: ScaleDegree,
  quality: ChordQuality,
  extensions: ChordExtensions,
  chromatic?: { isChromatic: true; chromaticType: 'borrowed' }
): GhostChordPayload {
  return {
    scaleDegree,
    quality,
    extensions: { ...extensions },
    ...(chromatic ?? {}),
  };
}

/** Builds the 'color' candidate from whichever grammar suggestion (expected or
 *  deceptive) lands on a mixture-eligible degree. Returns null rather than
 *  guessing when neither does — "omit rather than invent a random one". */
function mixtureCandidateFrom(suggestion: ChordSuggestion | undefined, mode: Mode): GhostCandidate | null {
  if (!suggestion || mode !== 'major') return null;
  const borrowedQuality = MIXTURE_TARGETS[suggestion.scaleDegree];
  if (!borrowedQuality) return null;

  return {
    chord: toPayload(suggestion.scaleDegree, borrowedQuality, {}, { isChromatic: true, chromaticType: 'borrowed' }),
    kind: 'color',
    label: 'borrowed light',
    weight: suggestion.confidence,
  };
}

/** The two sensible openers for an empty progression — no lastChord exists to
 *  run the grammar against, so this is a small hand-picked branch rather than
 *  a fabricated placeholder Chord fed through getSuggestions(). No color
 *  candidate: neither opener is a MIXTURE_TARGETS degree, so there is no
 *  idiomatic mixture to offer (consistent with the general omission rule,
 *  not a special case). */
function openingCandidates(mode: Mode): GhostCandidate[] {
  return [
    {
      chord: toPayload(1, getNaturalQuality(1, mode), {}),
      kind: 'expected',
      label: 'what the ear expects',
      weight: 1,
    },
    {
      chord: toPayload(5, getNaturalQuality(5, mode), {}),
      kind: 'deceptive',
      label: 'the road not taken',
      weight: 0.5,
    },
  ];
}

/**
 * Up to 3 candidates for what follows the last chord in `ctx.chords`, one per
 * kind when available. Deterministic — same input, same output.
 */
export function suggestNextChords(ctx: SuggestNextChordsContext): GhostCandidate[] {
  const { chords, key, mode } = ctx;

  if (chords.length === 0) {
    return openingCandidates(mode);
  }

  const lastChord = chords[chords.length - 1];
  const grammarSuggestions = getSuggestions({
    lastChord,
    previousChords: chords.slice(0, -1),
    key,
    mode,
  });

  const candidates: GhostCandidate[] = [];

  const expectedSuggestion = grammarSuggestions[0];
  if (expectedSuggestion) {
    candidates.push({
      chord: toPayload(expectedSuggestion.scaleDegree, expectedSuggestion.quality, expectedSuggestion.extensions),
      kind: 'expected',
      label: 'what the ear expects',
      weight: expectedSuggestion.confidence,
    });
  }

  // Rank 2 of the grammar: the deceptive resolution when the last chord is
  // dominant (both grammar tables literally rank V's deceptive-cadence entry
  // second), otherwise just the next-best continuation ("the road not
  // taken"). Because this only ever reads rank 0/1, a dominant chord that
  // was just reached deceptively (V -> vi) never gets suggested a return to
  // V here even though vi's own outgoing grammar row lists V third — rank 2
  // is simply never consulted, so the same deception is never re-proposed.
  const deceptiveSuggestion = grammarSuggestions[1];
  if (deceptiveSuggestion) {
    candidates.push({
      chord: toPayload(deceptiveSuggestion.scaleDegree, deceptiveSuggestion.quality, deceptiveSuggestion.extensions),
      kind: 'deceptive',
      label: isDominant(lastChord) ? 'the deception' : 'the road not taken',
      weight: deceptiveSuggestion.confidence,
    });
  }

  const colorCandidate =
    mixtureCandidateFrom(expectedSuggestion, mode) ?? mixtureCandidateFrom(deceptiveSuggestion, mode);
  if (colorCandidate) {
    candidates.push(colorCandidate);
  }

  return candidates;
}
